/**
 * AuthorMapView — LIM Level 1: SVG orbital mind-map per author + drill-in.
 *
 * Ported from ed-tool-philosophy/src/components/AuthorMapView.ts with these
 * literature-specific adaptations:
 *   - Central node = Author
 *   - Satellites = major works + theme treatments
 *   - Click a major work → rotate map so it lands at 3 o'clock, shift map
 *     left, display the work-section flowchart on the right (summary →
 *     structure → characters → themes → form_and_style → reception_and_legacy
 *     + nested passages). Each step expands to fill the viewport when tapped.
 *   - Click a theme treatment → emit a navigate event (caller decides)
 *   - Light palette (burgundy/ochre/teal) instead of philosophy's dark theme
 *
 * Architecture matches philosophy: viewport 1600×900 SVG, dual orbit if
 * >4 satellites, 80-iter collision resolver, rotation animated at 0.8s
 * cubic-bezier, drill panel uses foreignObject for HTML body.
 */

import { getDataService } from '@/services/data-registry';
import type { Author, MajorWork, ThemeTreatment, Passage } from '@/data/schema';
import { themeLabel, themeKind, nodeKindLabel, type UiLocale } from '@/data/vocab';
import { CATEGORY_COLORS } from '@/data/category-colors';
import { escapeXml as esc } from '@/core/escape';

/* ─────────────── Types ─────────────── */

interface GNode {
  id: string;
  label: string;
  sublabel?: string;
  type: 'central' | 'work' | 'theme' | 'movement' | 'genre';
  x: number;
  y: number;
  w: number;
  h: number;
  angle?: number;
  action?: { kind: 'work' | 'theme'; id: string };
}

interface GEdge { from: string; to: string; }

/* Light-palette per node type — tinte dalla PALETTE CANONICA per categoria
   (src/data/category-colors.ts): ogni tipo di pezzo ha la SUA tinta, identica
   in tutte le viste. I satelliti "tema" si specializzano per kind reale
   (movimento/genere/tema generico). */
const COLORS: Record<string, { bg: string; border: string; text: string }> = {
  central:  { bg: 'rgba(122, 31, 43, 0.10)', border: CATEGORY_COLORS.author, text: '#1a1814' },
  work:     { bg: 'rgba(184, 122, 42, 0.12)', border: CATEGORY_COLORS.work, text: '#1a1814' },
  theme:    { bg: 'rgba(29, 91, 107, 0.10)', border: CATEGORY_COLORS.theme, text: '#1a1814' },
  movement: { bg: 'rgba(93, 74, 126, 0.10)', border: CATEGORY_COLORS.movement, text: '#1a1814' },
  genre:    { bg: 'rgba(79, 115, 80, 0.10)', border: CATEGORY_COLORS.genre, text: '#1a1814' },
};

/* ─── Profondità: tinte chiare/scure per dare volume (carta + orbe + ombre).
   Stessa logica delle sfere-autore: la mappa non deve apparire "piatta". ─── */
function _rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** schiarisce verso il bianco (t=0 colore pieno, t=1 bianco). */
function tintW(hex: string, t: number): string {
  const [r, g, b] = _rgb(hex); const m = (c: number) => Math.round(c + (255 - c) * t);
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}
/** scurisce verso il nero. */
function shadeB(hex: string, t: number): string {
  const [r, g, b] = _rgb(hex); const m = (c: number) => Math.round(c * (1 - t));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}

/** <defs> condivisi: gradienti-carta per categoria, orbe centrale, ombre morbide.
 *  Inseriti DENTRO l'<svg> così il clone di cattura li conserva. */
function mapDefs(U: number): string {
  const cardGrads = (Object.keys(COLORS) as Array<keyof typeof COLORS>)
    .filter((k) => k !== 'central')
    .map((k) => `<linearGradient id="amv-card-${k}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${tintW(COLORS[k].border, 0.93)}"/>
        <stop offset="1" stop-color="${tintW(COLORS[k].border, 0.80)}"/>
      </linearGradient>`).join('');
  const author = CATEGORY_COLORS.author;
  const orb = `<radialGradient id="amv-orb" cx="38%" cy="30%" r="80%">
      <stop offset="0" stop-color="#ffffff"/>
      <stop offset="42%" stop-color="${tintW(author, 0.90)}"/>
      <stop offset="100%" stop-color="${tintW(author, 0.68)}"/>
    </radialGradient>
    <linearGradient id="amv-ring" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${tintW(author, 0.22)}"/>
      <stop offset="1" stop-color="${shadeB(author, 0.20)}"/>
    </linearGradient>`;
  const filt = `<filter id="amv-shadow" x="-40%" y="-40%" width="180%" height="180%">
      <feDropShadow dx="0" dy="${(U * 0.12).toFixed(1)}" stdDeviation="${(U * 0.2).toFixed(1)}" flood-color="#2a1c14" flood-opacity="0.22"/>
    </filter>
    <filter id="amv-orb-shadow" x="-70%" y="-70%" width="240%" height="240%">
      <feDropShadow dx="0" dy="${(U * 0.2).toFixed(1)}" stdDeviation="${(U * 0.5).toFixed(1)}" flood-color="#2a1c14" flood-opacity="0.20"/>
    </filter>`;
  return `<defs>${cardGrads}${orb}${filt}</defs>`;
}

/* SVG viewport — aspect CONTINUO: il viewBox segue le proporzioni del
   contenitore (clampate tra 9:16 e 16:9, area logica costante) → la mappa
   riempie telefono, tablet e PC senza bande vuote né effetto "schiacciato"
   (il binario 16:9 / 9:16 lasciava il tablet 3:4 con una colonna stretta). */
type MapDims = { VW: number; VH: number; CX: number; CY: number };
const BASE_AREA = 1600 * 900;
function dimsForAspect(aspect: number): MapDims {
  const a = Math.min(16 / 9, Math.max(9 / 16, aspect || 16 / 9));
  const VW = Math.round(Math.sqrt(BASE_AREA * a));
  const VH = Math.round(VW / a);
  return { VW, VH, CX: VW / 2, CY: VH / 2 };
}

/* Satellite size table, in U units (U = node-radius unit, set per-graph) */
const SIZE_TABLE = {
  central: { wU: 10.0, hU: 10.0 },
  work:    { wU: 13.0, hU: 5.2 },
  theme:   { wU: 11.5, hU: 4.6 },
} as const;

const FONT_TABLE: Record<string, { label: number; sublabel: number; action: number }> = {
  central: { label: 2.0,  sublabel: 1.0,  action: 0 },
  work:    { label: 1.2,  sublabel: 0,    action: 0.88 },
  theme:   { label: 1.2,  sublabel: 0,    action: 0.88 },
  /* movement/genre ereditano da theme via fallback nel renderer */
};

/* ─────────────── Helpers ─────────────── */


function wrapLines(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    if (line.length + w.length + 1 > maxChars && line) {
      lines.push(line);
      if (lines.length >= maxLines) return lines;
      line = w;
    } else {
      line += (line ? ' ' : '') + w;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

function shortName(fullName: string): string {
  // Nomi con parentetica (es. "Anonymous (the Beowulf poet)") → la parte prima
  // della parentesi ("Anonymous"), così il nodo centrale non mostra "poet)".
  // Altrimenti il cognome (ultima parola): "Geoffrey Chaucer" → "Chaucer".
  const base = ((fullName.split('(')[0] ?? fullName).trim()) || fullName;
  const parts = base.split(/\s+/);
  return parts.length <= 1 ? base : (parts[parts.length - 1] ?? base);
}

/** Strip HTML inline tags, used for one-line preview text on satellite cards. */
function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

/** SVG di fallback per la cattura-RAMO (il reader moderno usa `data.nodes`;
 *  questo serve a client legacy/lightbox). Tinte = colori-categoria standard. */
function branchFallbackSvg(
  bNodes: MapGraphSnapshot['nodes'],
  bEdges: Array<{ from: string; to: string }>,
  vb: [number, number, number, number],
): string {
  const byId = new Map(bNodes.map((n) => [n.id, n]));
  const tint = (t: string): string =>
    t === 'central' ? CATEGORY_COLORS.author
    : t === 'work' ? CATEGORY_COLORS.work
    : t === 'movement' ? CATEGORY_COLORS.movement
    : t === 'genre' ? CATEGORY_COLORS.genre
    : CATEGORY_COLORS.theme;
  const lines = bEdges.map((e) => {
    const a = byId.get(e.from), b = byId.get(e.to);
    if (!a || !b) return '';
    return `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="rgba(20,18,16,0.22)" stroke-width="2.5"/>`;
  }).join('');
  const boxes = bNodes.map((n) => {
    const c = tint(n.type);
    const label = n.label.length > 30 ? `${n.label.slice(0, 29)}…` : n.label;
    if (n.type === 'central') {
      return `<circle cx="${n.x}" cy="${n.y}" r="${n.w / 2}" fill="#f6e9eb" stroke="${c}" stroke-width="3"/>
        <text x="${n.x}" y="${n.y + 9}" text-anchor="middle" font-family="Fraunces,serif" font-size="30" font-weight="700" fill="#1a1814">${esc(label)}</text>`;
    }
    return `<rect x="${n.x - n.w / 2}" y="${n.y - n.h / 2}" width="${n.w}" height="${n.h}" rx="16" fill="#fdfcfa" stroke="${c}" stroke-width="2.5"/>
      <text x="${n.x}" y="${n.y + 8}" text-anchor="middle" font-family="Fraunces,serif" font-size="24" font-weight="600" fill="#1a1814">${esc(label)}</text>`;
  }).join('');
  return `<svg viewBox="${vb.join(' ')}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">${lines}${boxes}</svg>`;
}

function shortSummary(html: string): string {
  const clean = stripHtml(html);
  if (clean.length <= 90) return clean;
  return clean.slice(0, 85).replace(/\s+\S*$/, '') + '…';
}

/* ─────────────── Build graph ─────────────── */

function buildGraph(author: Author, locale: UiLocale, dims: MapDims): { nodes: GNode[]; edges: GEdge[]; U: number } {
  const { VW, VH, CX, CY } = dims;
  const nodes: GNode[] = [];
  const edges: GEdge[] = [];

  type Sat = Omit<GNode, 'x' | 'y' | 'w' | 'h'> & { sizeKey: keyof typeof SIZE_TABLE; priority: number };
  const sats: Sat[] = [];

  // Major works first (priority 1)
  for (const w of author.works) {
    if (!w.is_major) continue;
    sats.push({
      id: `work-${w.id}`,
      label: w.title,
      type: 'work',
      sizeKey: 'work',
      priority: 1,
      action: { kind: 'work', id: w.id },
    });
  }
  // Theme treatments (priority 2) — il TIPO segue la categoria reale del tema
  // (movimento/genere/tema generico) → tinta canonica corrispondente.
  for (const t of author.theme_treatments) {
    const k = themeKind(t.theme_id);
    sats.push({
      id: `theme-${t.theme_id}`,
      label: themeLabel(t.theme_id, locale),
      type: k === 'movement' ? 'movement' : k === 'genre' ? 'genre' : 'theme',
      sizeKey: 'theme',
      priority: 2,
      action: { kind: 'theme', id: t.theme_id },
    });
  }

  sats.sort((a, b) => a.priority - b.priority);
  const N = sats.length;
  // U scales with how many satellites we have (smaller boxes when crowded).
  const U = N <= 4 ? 32 : N <= 7 ? 30 : N <= 10 ? 27 : 24;

  // Central author node
  const cSize = SIZE_TABLE.central;
  const dates = author.birth_year && author.death_year
    ? `${author.birth_year}–${author.death_year}`
    : '';
  nodes.push({
    id: 'center',
    label: shortName(author.name),
    sublabel: dates,
    type: 'central',
    x: CX, y: CY, angle: 0,
    w: cSize.wU * U, h: cSize.hU * U,
  });

  const satSizes = sats.map(s => {
    const sz = SIZE_TABLE[s.sizeKey];
    return { w: sz.wU * U, h: sz.hU * U };
  });
  const maxSatW = Math.max(...satSizes.map(s => s.w), 0);
  const maxSatH = Math.max(...satSizes.map(s => s.h), 0);

  const centralRx = cSize.wU * U / 2;
  const centralRy = cSize.hU * U / 2;
  const outerRx = CX - maxSatW / 2 - U * 1.4;
  const outerRy = CY - maxSatH / 2 - U * 1.4;
  const innerRx = Math.max(outerRx * 0.58, centralRx + maxSatW / 2 + U * 1.5);
  const innerRy = Math.max(outerRy * 0.58, centralRy + maxSatH / 2 + U * 1.5);
  const useSingleOrbit = N <= 4;

  const startAngle = -Math.PI / 2;
  for (let i = 0; i < N; i++) {
    const s = sats[i];
    const angle = startAngle + (2 * Math.PI * i) / N;
    let rx: number, ry: number;
    if (useSingleOrbit) {
      rx = (innerRx + outerRx) / 2;
      ry = (innerRy + outerRy) / 2;
    } else {
      const isOuter = i % 2 === 1;
      rx = isOuter ? outerRx : innerRx;
      ry = isOuter ? outerRy : innerRy;
    }
    const x = CX + rx * Math.cos(angle);
    const y = CY + ry * Math.sin(angle);
    const sz = satSizes[i];
    nodes.push({ ...s, x, y, w: sz.w, h: sz.h, angle, sizeKey: undefined } as unknown as GNode);
    edges.push({ from: 'center', to: s.id });
  }

  // Collision resolver (80 iterations, push-apart on overlap)
  const gap = U * 0.6;
  for (let iter = 0; iter < 80; iter++) {
    let moved = false;
    for (let i = 1; i < nodes.length; i++) {
      const a = nodes[i];
      for (let j = 0; j < nodes.length; j++) {
        if (i === j) continue;
        const b = nodes[j];
        const overlapX = (a.w / 2 + b.w / 2 + gap) - Math.abs(a.x - b.x);
        const overlapY = (a.h / 2 + b.h / 2 + gap) - Math.abs(a.y - b.y);
        if (overlapX <= 0 || overlapY <= 0) continue;
        const pushAxis = overlapX < overlapY ? 'x' : 'y';
        const pushAmount = (pushAxis === 'x' ? overlapX : overlapY) * 0.5;
        if (j === 0) {
          const dx = a.x - CX || 0.1; const dy = a.y - CY || 0.1;
          if (pushAxis === 'x') a.x += Math.sign(dx) * pushAmount;
          else a.y += Math.sign(dy) * pushAmount;
        } else {
          const dx = a.x - b.x || 0.1; const dy = a.y - b.y || 0.1;
          const half = pushAmount * 0.5;
          if (pushAxis === 'x') { a.x += Math.sign(dx) * half; b.x -= Math.sign(dx) * half; }
          else { a.y += Math.sign(dy) * half; b.y -= Math.sign(dy) * half; }
        }
        moved = true;
      }
      // Margine dal BORDO più generoso del gap tra nodi: i box non devono
      // toccare i lati dello schermo (su telefono restavano attaccati a sx).
      const edge = U * 1.4;
      a.x = Math.max(a.w / 2 + edge, Math.min(VW - a.w / 2 - edge, a.x));
      a.y = Math.max(a.h / 2 + edge, Math.min(VH - a.h / 2 - edge, a.y));
    }
    if (!moved) break;
  }
  // Recompute actual angles after collision resolver
  for (let i = 1; i < nodes.length; i++) {
    nodes[i].angle = Math.atan2(nodes[i].y - CY, nodes[i].x - CX);
  }
  return { nodes, edges, U };
}

/* ─────────────── SVG Renderer ─────────────── */

function renderGraph(nodes: GNode[], edges: GEdge[], U: number, dims: MapDims, locale: UiLocale): string {
  const { VW, VH, CX, CY } = dims;
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  let edgeSvg = '';
  for (const e of edges) {
    const from = nodeMap.get(e.from)!;
    const to = nodeMap.get(e.to)!;
    const dx = to.x - from.x, dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dx / dist, ny = dy / dist;
    // Distanza dal centro al BORDO del nodo lungo la direzione (nx,ny): cerchio per
    // il nodo centrale, rettangolo per i box. Così la linea si ferma sul bordo e
    // NON entra nel box (altrimenti, col fill semi-trasparente, attraverserebbe il titolo).
    const edgeR = (node: GNode): number => {
      if (node.type === 'central') return Math.min(node.w, node.h) / 2;
      const ax = Math.abs(nx) || 1e-6, ay = Math.abs(ny) || 1e-6;
      return Math.min((node.w / 2) / ax, (node.h / 2) / ay);
    };
    const fromR = edgeR(from);
    const toR = edgeR(to);
    const x1 = from.x + nx * fromR, y1 = from.y + ny * fromR;
    const x2 = to.x - nx * toR, y2 = to.y - ny * toR;
    // raggio tinto per categoria del nodo di arrivo: alone tenue + tratto pieno
    // → il connettore "aggancia" il nodo invece di essere un filo grigio piatto.
    const ec = (COLORS[to.type] ?? COLORS.theme).border;
    const sw = U * 0.07;
    const attrs = `data-edge-from="${e.from}" data-edge-to="${e.to}"`;
    edgeSvg += `<line ${attrs} x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${ec}" stroke-opacity="0.14" stroke-width="${sw * 2.4}" stroke-linecap="round" />`
      + `<line ${attrs} x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${ec}" stroke-opacity="0.45" stroke-width="${sw}" stroke-linecap="round" />`;
  }

  let nodeSvg = '';
  for (const n of nodes) {
    const c = COLORS[n.type] ?? COLORS.theme;
    const isCircle = n.type === 'central';
    const isClickable = Boolean(n.action);
    const dataAttrs = [
      `data-node-id="${n.id}"`,
      `data-node-type="${n.type}"`,
      n.action ? `data-action="${n.action.kind}" data-action-id="${n.action.id}"` : '',
    ].filter(Boolean).join(' ');
    const cursor = isClickable ? 'pointer' : 'default';
    const fonts = FONT_TABLE[n.type] ?? FONT_TABLE.theme;

    if (isCircle) {
      const r = n.w / 2;
      const labelFs = fonts.label * U;
      const subFs = fonts.sublabel * U;
      const ringW = U * 0.11;
      nodeSvg += `
        <g class="graph-node" ${dataAttrs} style="cursor:${cursor}">
          <circle cx="${n.x}" cy="${n.y}" r="${r}" fill="url(#amv-orb)" filter="url(#amv-orb-shadow)" />
          <circle cx="${n.x}" cy="${n.y}" r="${r - ringW / 2}" fill="none" stroke="url(#amv-ring)" stroke-width="${ringW}" />
          <path d="M ${n.x - r * 0.56} ${n.y - r * 0.7} A ${r * 0.88} ${r * 0.88} 0 0 1 ${n.x + r * 0.56} ${n.y - r * 0.7}"
                fill="none" stroke="rgba(255,255,255,0.72)" stroke-width="${r * 0.05}" stroke-linecap="round" pointer-events="none" />
          <text x="${n.x}" y="${n.y - U * 0.15}" text-anchor="middle" fill="${c.text}" font-size="${labelFs}" font-weight="800" font-family="Fraunces, serif">${esc(n.label)}</text>
          ${n.sublabel ? `<text x="${n.x}" y="${n.y + U * 0.7}" text-anchor="middle" fill="${shadeB(c.border, 0.1)}" font-size="${subFs}" font-family="Inter, sans-serif" letter-spacing="0.04em">${esc(n.sublabel)}</text>` : ''}
        </g>`;
    } else {
      const labelFs = fonts.label * U;
      const actionFs = fonts.action * U;
      const lineH = labelFs * 1.25;
      const lines = wrapLines(n.label, 22, 2);
      const categoryLabel = nodeKindLabel(n.type, locale) + ' ▶';
      const subtitleGap = actionFs * 0.35;
      const subtitleH = actionFs + subtitleGap;
      const totalTextH = lines.length * lineH + subtitleH;
      const blockTop = n.y - totalTextH / 2;
      const firstLineY = blockTop + labelFs * 0.35;
      const rx = U * 0.45;
      const sw = U * 0.06;
      const topY = n.y - n.h / 2;

      nodeSvg += `
        <g class="graph-node" ${dataAttrs} style="cursor:${cursor}">
          <rect x="${n.x - n.w / 2}" y="${topY}" width="${n.w}" height="${n.h}" rx="${rx}" fill="url(#amv-card-${n.type})" stroke="${c.border}" stroke-width="${sw}" filter="url(#amv-shadow)" />
          <line x1="${n.x - n.w / 2 + rx}" y1="${topY + sw * 1.4}" x2="${n.x + n.w / 2 - rx}" y2="${topY + sw * 1.4}" stroke="rgba(255,255,255,0.65)" stroke-width="${U * 0.045}" stroke-linecap="round" pointer-events="none" />
          ${lines.map((line, li) =>
            `<text x="${n.x}" y="${firstLineY + li * lineH}" text-anchor="middle" fill="${c.text}" font-size="${labelFs}" font-weight="600" font-family="Fraunces, serif">${esc(line)}</text>`
          ).join('')}
          <text x="${n.x}" y="${firstLineY + lines.length * lineH + subtitleGap}" text-anchor="middle" fill="${c.border}" font-size="${actionFs}" opacity="0.75" font-family="Inter, sans-serif">${categoryLabel}</text>
        </g>`;
    }
  }

  return `
    <svg viewBox="0 0 ${VW} ${VH}"
         preserveAspectRatio="xMidYMid meet"
         xmlns="http://www.w3.org/2000/svg"
         class="author-map-svg"
         style="width:100%;height:100%;">
      ${mapDefs(U)}
      <g class="map-root" style="transform-origin:${CX}px ${CY}px;transition:transform 0.8s cubic-bezier(0.4,0,0.2,1)">
        <g class="edges-layer">${edgeSvg}</g>
        <g class="nodes-layer">${nodeSvg}</g>
        <g class="overlay-layer"></g>
      </g>
      <g class="steps-layer"></g>
    </svg>
  `;
}

/* ─────────────── Step data builder ─────────────── */

interface StepDatum {
  num: string;
  title: string;
  body: string;       // HTML inline (paragraphs)
  summary: string;    // truncated preview for collapsed view
  isClimax: boolean;  // styled differently (reception/legacy = climax)
}

// Titoli delle sezioni di approfondimento opera (vista Slide/Mappa). Erano
// stringhe inglesi hardcoded anche con UI in italiano; ora seguono il locale.
const STEP_LABELS: Record<string, { it: string; en: string }> = {
  glance:     { it: 'In sintesi',           en: 'At a glance' },
  summary:    { it: 'Trama',                 en: 'Summary' },
  structure:  { it: 'Struttura',             en: 'Structure' },
  characters: { it: 'Personaggi',            en: 'Characters' },
  themes:     { it: 'Temi',                  en: 'Themes' },
  formStyle:  { it: 'Forma e stile',         en: 'Form & style' },
  reception:  { it: 'Ricezione ed eredità',  en: 'Reception & legacy' },
  passages:   { it: 'Brani',                 en: 'Passages' },
};
function stepLabel(key: string, locale: UiLocale): string {
  const e = STEP_LABELS[key];
  return e ? (locale === 'en' ? e.en : e.it) : key;
}

function buildWorkSteps(work: MajorWork, locale: UiLocale): StepDatum[] {
  const steps: StepDatum[] = [];
  const add = (n: string, t: string, html: string, climax = false) => {
    if (!html) return;
    steps.push({
      num: n,
      title: t,
      body: html,
      summary: shortSummary(html),
      isClimax: climax,
    });
  };
  // STANDARD map_blurb (docs/standards/00-map-blurb.md) + progressive
  // disclosure (scelta utente 2026-06-11): a riposo il box mostra SOLO la
  // sintesi-da-mappa (fallback: one_line); il testo completo dell'opera
  // appare SOLO espandendo il box col click (e si richiude col click).
  const glance = work.map_blurb ?? work.one_line;
  const section = (title: string, html?: string | null) => html
    ? `<div style="font-family:Fraunces,serif;font-size:0.8em;font-weight:700;color:#b87a2a;letter-spacing:0.04em;text-transform:uppercase;margin:1.4em 0 0.4em">${title}</div>${html}`
    : '';
  steps.push({
    num: '1',
    title: stepLabel('glance', locale),
    body: `<p><em>${esc(glance)}</em></p>`
      + section(stepLabel('summary', locale), work.summary)
      + section(stepLabel('structure', locale), work.structure)
      + section(stepLabel('characters', locale), work.characters)
      + section(stepLabel('themes', locale), work.themes)
      + section(stepLabel('formStyle', locale), work.form_and_style)
      + section(stepLabel('reception', locale), work.reception_and_legacy),
    summary: glance, // il blurb È la sintesi: intero anche da compresso
    isClimax: false,
  });
  if (work.passages && work.passages.length > 0) {
    const passagesHtml = work.passages.map(p => `
      <blockquote style="border-left:3px solid #7a1f2b;padding-left:1em;margin:0 0 1em;font-style:italic">
        ${esc(p.text).replace(/\n/g, '<br>')}
        <footer style="font-style:normal;color:#8a857d;font-size:0.85em;margin-top:0.5em">— ${esc(p.reference)}</footer>
      </blockquote>
      <p style="font-size:0.9em;color:#4a4640;margin:0 0 1.5em">${esc(p.note)}</p>
    `).join('');
    add('★', stepLabel('passages', locale), passagesHtml);
  }
  return steps;
}

function buildThemeSteps(t: ThemeTreatment, work: MajorWork | null, passage: Passage | null, locale: UiLocale): StepDatum[] {
  const steps: StepDatum[] = [];
  // STANDARD map_blurb + progressive disclosure: il box a riposo mostra la
  // sintesi scritta per la mappa (mai troncata con ellissi); il click espande
  // al testo COMPLETO di quella parte, il ri-click richiude alla sintesi.
  steps.push({
    num: '1',
    title: themeLabel(t.theme_id, locale),
    body: t.explanation,
    summary: t.map_blurb ?? shortSummary(t.explanation),
    isClimax: false,
  });
  if (work) {
    const wGlance = work.map_blurb ?? work.one_line;
    steps.push({
      num: '2',
      title: `Key work: ${work.title}`,
      body: `<p><em>${esc(wGlance)}</em></p>${work.summary ?? ''}`,
      summary: wGlance,
      isClimax: false,
    });
  }
  if (passage) {
    steps.push({
      num: '★',
      title: 'Passage',
      body: `
        <blockquote style="border-left:3px solid #7a1f2b;padding-left:1em;margin:0 0 1em;font-style:italic">
          ${esc(passage.text).replace(/\n/g, '<br>')}
          <footer style="font-style:normal;color:#8a857d;font-size:0.85em;margin-top:0.5em">— ${esc(passage.reference)}</footer>
        </blockquote>
        <p style="font-size:0.9em;color:#4a4640;margin:0">${esc(passage.note)}</p>
      `,
      summary: shortSummary(passage.text),
      isClimax: true,
    });
  }
  return steps;
}

/* ─────────────── Public mount ─────────────── */

/** Snapshot strutturato del grafo, per costruire il payload `data` del blocco 'map'. */
export interface MapGraphSnapshot {
  kind: string;
  authorId: string;
  locale: string;
  viewBox: [number, number, number, number];
  /** `note` = sintesi-da-mappa del nodo (map_blurb): il reader del hub la
   *  mostra in tendina al click. */
  nodes: Array<{ id: string; label: string; type: string; x: number; y: number; w: number; h: number; note?: string }>;
  edges: Array<{ from: string; to: string }>;
}

/** Cattura pronta per il blocco 'map': SVG ritagliato + payload strutturato. */
export interface MapCapture {
  content_html: string;
  data: Record<string, unknown>;
}

/** API di selezione "a pezzi" multi-livello esposta al lesson-builder (modalità docente). */
export interface MapSelectionApi {
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  isSelectMode: () => boolean;
  /** Id selezionati del livello corrente (nodi, o `step:idx` nel drill-in). */
  getSelectedIds: () => string[];
  clearSelection: () => void;
  onSelectionChange: (cb: (ids: string[]) => void) => void;
  getGraph: () => MapGraphSnapshot;
  /** Costruisce la cattura del livello corrente; null se nulla è selezionato. */
  buildCapture: () => MapCapture | null;
}

export interface AuthorMapHandle {
  dispose: () => void;
  /** Chiude l'approfondimento (drill-in) e torna alla mappa d'insieme,
   *  SENZA uscire dalla modalità Mappa. True se c'era qualcosa da chiudere. */
  reset: () => boolean;
  /** Notifica quando si entra/esce dal drill-in (per mostrare/nascondere ✕). */
  onDrillChange: (cb: (drilled: boolean) => void) => void;
  selection: MapSelectionApi;
}

export async function mountAuthorMap(
  container: HTMLElement,
  authorId: string,
  locale: UiLocale,
  onNavigate: (action: { kind: string; id?: string }) => void,
): Promise<AuthorMapHandle> {
  const data = getDataService();
  // La mappa vive dentro la scheda, ma si carica il dossier da sé: così regge
  // anche se viene montata per prima (modo LIM) e non dipende dall'ordine.
  const author = await data.loadAuthor(authorId);

  // Proporzioni dal CONTENITORE (fallback: finestra): il viewBox segue
  // l'aspect reale → la mappa riempie qualsiasi schermo (telefono/tablet/PC).
  const cw = container.clientWidth || window.innerWidth;
  const ch = container.clientHeight || window.innerHeight;
  const portrait = ch > cw; // asse del drill-in (verticale se più alto che largo)
  const dims = dimsForAspect(cw / ch);
  const { VW, VH, CX, CY } = dims;

  if (!author) {
    container.innerHTML = '<p style="padding:2em">Author not found</p>';
    return {
      dispose: () => { container.innerHTML = ''; },
      reset: () => false,
      onDrillChange: () => {},
      selection: {
        enterSelectMode: () => {}, exitSelectMode: () => {}, isSelectMode: () => false,
        getSelectedIds: () => [], clearSelection: () => {}, onSelectionChange: () => {},
        getGraph: () => ({ kind: 'author-map', authorId, locale, viewBox: [0, 0, VW, VH], nodes: [], edges: [] }),
        buildCapture: () => null,
      },
    };
  }

  const { nodes, edges, U } = buildGraph(author, locale, dims);

  container.innerHTML = `
    <div class="author-map-container author-map-view" data-map-view>
      <div class="author-map-viewport">
        ${renderGraph(nodes, edges, U, dims, locale)}
      </div>
    </div>
  `;

  const svg = container.querySelector<SVGSVGElement>('.author-map-svg')!;
  const nodesLayer = svg.querySelector('.nodes-layer')!;
  const stepsLayer = svg.querySelector<SVGGElement>('.steps-layer')!;
  const overlayLayer = svg.querySelector('.overlay-layer')!;
  const nodeDataMap = new Map(nodes.map(n => [n.id, n]));

  let focusedId: string | null = null;
  let drilledKind: 'work' | 'theme' | null = null;
  let drilledActionId: string | null = null;
  let stepData: StepDatum[] = [];
  let expandedStepIdx: number | null = null;
  let stepsBuilt = false;
  const drillListeners: Array<(drilled: boolean) => void> = [];
  function notifyDrill(drilled: boolean): void {
    drillListeners.forEach((cb) => cb(drilled));
  }

  /** Sintesi-da-mappa del nodo (map_blurb / fallback): finisce nel campo
   *  `note` delle catture → tendina cliccabile nel reader del hub. */
  function noteFor(n: GNode): string | undefined {
    if (n.action?.kind === 'work') {
      const w = author!.works.find((x) => x.id === n.action!.id);
      return w?.map_blurb ?? w?.one_line;
    }
    if (n.action?.kind === 'theme') {
      return author!.theme_treatments.find((x) => x.theme_id === n.action!.id)?.map_blurb;
    }
    if (n.type === 'central') return author!.tagline;
    return undefined;
  }

  // ── Selezione "a pezzi" MULTI-LIVELLO (modalità docente) ─────────────
  // Livello mappa: i click sui nodi-foglia selezionano; i nodi con drill-in
  // (opera/tema) ENTRANO. Livello drill-in: i click sugli step selezionano.
  // "Aggiungi selezione" cattura il livello corrente (buildCapture()).
  let selectMode = false;
  const selectedIds = new Set<string>();      // nodi mappa selezionati
  const selectedSteps = new Set<number>();    // step drill-in selezionati
  let drilledNodeId: string | null = null;    // nodo da cui si è entrati
  let onSelChange: ((ids: string[]) => void) | null = null;

  /** Id di selezione del livello corrente (nodi, oppure step:idx nel drill-in). */
  function currentSelectionIds(): string[] {
    return drilledKind
      ? Array.from(selectedSteps).sort((a, b) => a - b).map((i) => `step:${i}`)
      : Array.from(selectedIds);
  }

  function styleNodeSelection(): void {
    const anySel = selectedIds.size > 0;
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      const id = g.dataset.nodeId!;
      const sel = selectedIds.has(id);
      g.style.filter = sel ? 'drop-shadow(0 0 16px rgba(122,31,43,0.6))' : '';
      g.style.opacity = !selectMode || !anySel || sel ? '1' : '0.3';
    });
  }
  function styleStepSelection(): void {
    const anySel = selectedSteps.size > 0;
    stepsLayer.querySelectorAll<SVGGElement>('.step-box').forEach((g) => {
      const idx = Number(g.dataset.stepIdx);
      const sel = selectedSteps.has(idx);
      const rect = g.querySelector<SVGRectElement>('.step-rect');
      if (rect) rect.style.filter = sel ? 'drop-shadow(0 0 12px rgba(122,31,43,0.75))' : '';
      g.style.opacity = !selectMode || !anySel || sel ? '1' : '0.45';
    });
  }
  function clearSelectionStyles(): void {
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.filter = ''; g.style.opacity = '1';
    });
    stepsLayer.querySelectorAll<SVGGElement>('.step-box').forEach((g) => {
      const rect = g.querySelector<SVGRectElement>('.step-rect');
      if (rect) rect.style.filter = '';
      g.style.opacity = '1';
    });
  }

  /** Clona l'SVG ripulendo stili inline (selezione/animazioni) per una cattura statica. */
  function cleanCloneForCapture(): SVGSVGElement {
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.filter = ''; g.style.opacity = ''; g.style.transform = '';
    });
    clone.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.opacity = ''; l.style.transform = '';
    });
    clone.querySelectorAll<SVGGElement>('.step-box').forEach((g) => {
      const rect = g.querySelector<SVGRectElement>('.step-rect');
      if (rect) { rect.style.filter = ''; rect.style.fill = ''; }
      g.style.opacity = '';
    });
    if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return clone;
  }

  /** Bounding box + padding, con viewBox MINIMO (evita che 1 pezzo piccolo venga scalato enorme). */
  function bbox(items: Array<{ x: number; y: number; w: number; h: number }>, pad: number): [number, number, number, number] {
    const minX = Math.min(...items.map((r) => r.x));
    const minY = Math.min(...items.map((r) => r.y));
    const maxX = Math.max(...items.map((r) => r.x + r.w));
    const maxY = Math.max(...items.map((r) => r.y + r.h));
    let x = minX - pad, y = minY - pad;
    let w = (maxX - minX) + 2 * pad, h = (maxY - minY) + 2 * pad;
    const MIN_W = VW / 3, MIN_H = VH / 3;
    if (w < MIN_W) { x -= (MIN_W - w) / 2; w = MIN_W; }
    if (h < MIN_H) { y -= (MIN_H - h) / 2; h = MIN_H; }
    return [x, y, w, h];
  }

  /** Costruisce content_html (SVG ritagliato) + data per il livello corrente. */
  function buildCapture(): { content_html: string; data: Record<string, unknown> } | null {
    // Livello drill-in → step selezionati = cattura del RAMO (scelta utente
    // 2026-06-11): non il ritaglio fotografico dei box (illeggibile nella
    // lezione), ma una mini-mappa sintetica autore → nodo → parti scelte,
    // così si VEDE da dove proviene ogni pezzo (è il proseguio della mappa).
    if (drilledKind) {
      if (selectedSteps.size === 0) return null;
      const branch = nodes.find((n) => n.id === drilledNodeId);
      const central = nodes.find((n) => n.type === 'central');
      if (!branch) return null;
      const stepsSel = Array.from(selectedSteps).sort((a, b) => a - b)
        .map((i) => ({
          idx: i,
          // "1 · Titolo": numerazione = ordine delle parti nel ramo (ed evita
          // il doppione visivo quando lo step si chiama come il nodo).
          title: `${stepData[i]?.num ?? i + 1} · ${stepData[i]?.title ?? ''}`,
          summary: stepData[i]?.summary ?? '',
        }));

      // Geometria sintetica del ramo: autore (sx) → nodo (centro) → step (dx, colonna).
      const SW = 380, SH = 120, GAP = 40;
      const colH = stepsSel.length * (SH + GAP) - GAP;
      const H = Math.max(colH + 120, 420);
      const W = 1280;
      const stepX = 1020;
      const startY = (H - colH) / 2 + SH / 2;
      const bNodes: MapGraphSnapshot['nodes'] = [
        ...(central ? [{
          id: 'center', label: central.label, type: 'central',
          x: 150, y: H / 2, w: 190, h: 190, note: noteFor(central),
        }] : []),
        {
          id: branch.id, label: branch.label, type: branch.type,
          x: 520, y: H / 2, w: 280, h: 110, note: noteFor(branch),
        },
        // Gli step ereditano il TIPO del ramo → stessa tinta-categoria nel
        // reader = continuità visiva col nodo da cui provengono.
        ...stepsSel.map((s, i) => ({
          id: `step-${s.idx}`, label: s.title, type: branch.type,
          x: stepX, y: startY + i * (SH + GAP), w: SW, h: SH, note: s.summary || undefined,
        })),
      ];
      const bEdges = [
        ...(central ? [{ from: 'center', to: branch.id }] : []),
        ...stepsSel.map((s) => ({ from: branch.id, to: `step-${s.idx}` })),
      ];
      const vb: [number, number, number, number] = [0, 0, W, H];
      return {
        content_html: `<figure class="lb-map">${branchFallbackSvg(bNodes, bEdges, vb)}</figure>`,
        data: {
          kind: 'author-map', author_id: authorId, locale, selection: 'branch',
          drill: { actionKind: drilledKind, actionId: drilledActionId, nodeId: drilledNodeId },
          steps: stepsSel.map(({ idx, title }) => ({ idx, title })),
          nodes: bNodes, edges: bEdges, viewBox: vb,
        },
      };
    }
    // Livello mappa → nodi selezionati
    if (selectedIds.size === 0) return null;
    const idSet = selectedIds;
    const selNodes = nodes.filter((n) => idSet.has(n.id));
    const selEdges = edges.filter((e) => idSet.has(e.from) && idSet.has(e.to));
    const clone = cleanCloneForCapture();
    const sl = clone.querySelector('.steps-layer');
    if (sl) sl.innerHTML = '';
    clone.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      if (!idSet.has(g.dataset.nodeId ?? '')) g.remove();
    });
    clone.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      if (!(idSet.has(l.dataset.edgeFrom ?? '') && idSet.has(l.dataset.edgeTo ?? ''))) l.remove();
    });
    const vb = bbox(selNodes.map((n) => ({ x: n.x - n.w / 2, y: n.y - n.h / 2, w: n.w, h: n.h })), 24);
    clone.setAttribute('viewBox', vb.join(' '));
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    return {
      content_html: `<figure class="lb-map">${clone.outerHTML}</figure>`,
      data: {
        kind: 'author-map', author_id: authorId, locale, selection: 'subset',
        node_ids: Array.from(idSet),
        nodes: selNodes.map((n) => ({
          id: n.id, label: n.label, type: n.type, x: n.x, y: n.y, w: n.w, h: n.h,
          ...(noteFor(n) ? { note: noteFor(n) } : {}),
        })),
        edges: selEdges, viewBox: vb,
      },
    };
  }

  // Drill-in: in ORIZZONTALE la mappa scivola a sinistra e gli step occupano
  // la metà destra; in VERTICALE (telefono) la mappa scivola in ALTO e gli
  // step occupano la fascia bassa a tutta larghezza.
  const MAP_SHIFT_X = portrait ? 0 : VW * 0.55;
  const MAP_SHIFT_Y = portrait ? VH * 0.55 : 0;
  const STEPS_LEFT = portrait ? U * 1 : VW * 0.48;
  const STEPS_RIGHT = VW - U * 1;
  const STEPS_TOP = portrait ? VH * 0.48 : U * 1;
  const STEPS_BOTTOM = VH - U * 1;

  function onClick(ev: Event): void {
    // Modalità selezione MULTI-LIVELLO.
    if (selectMode) {
      if (drilledKind) {
        // Dentro un pezzo: seleziona gli step; click sul vuoto → risali.
        const stepEl = (ev.target as SVGElement).closest<SVGGElement>('.step-box');
        if (stepEl) {
          const idx = Number(stepEl.dataset.stepIdx);
          if (selectedSteps.has(idx)) selectedSteps.delete(idx);
          else selectedSteps.add(idx);
          styleStepSelection();
          onSelChange?.(currentSelectionIds());
          return;
        }
        if (!(ev.target as SVGElement).closest('.graph-node')) {
          closeDrillIn();
          selectedSteps.clear();
          onSelChange?.(currentSelectionIds());
          styleNodeSelection();
        }
        return;
      }
      // Livello mappa: OGNI nodo si seleziona/deseleziona — lo scopo della
      // selezione è COMPORRE la mini-mappa (nodi+geometria reale) per la
      // lezione. Prima i nodi work/theme entravano nel drill-in e si finiva
      // per catturare la fascia step (ritaglio verticale illeggibile nella
      // lezione). L'approfondimento si fa FUORI dalla modalità selezione.
      const node = (ev.target as SVGElement).closest<SVGGElement>('.graph-node');
      if (!node) return;
      const id = node.dataset.nodeId!;
      if (selectedIds.has(id)) selectedIds.delete(id);
      else selectedIds.add(id);
      styleNodeSelection();
      onSelChange?.(currentSelectionIds());
      return;
    }
    const target = (ev.target as SVGElement).closest<SVGGElement>('.graph-node');
    const stepEl = (ev.target as SVGElement).closest<SVGGElement>('.step-box');
    if (stepEl) { toggleStep(stepEl); return; }
    if (!target) {
      if (drilledKind) closeDrillIn();
      else resetFocus();
      return;
    }
    const nodeId = target.dataset.nodeId!;
    const actKind = target.dataset.action as 'work' | 'theme' | undefined;
    const actId = target.dataset.actionId;

    if (actKind === 'work' && actId) {
      if (drilledKind === 'work' && drilledActionId === actId) closeDrillIn();
      else openDrillIn('work', actId, nodeId);
      return;
    }
    if (actKind === 'theme' && actId) {
      if (drilledKind === 'theme' && drilledActionId === actId) closeDrillIn();
      else openDrillIn('theme', actId, nodeId);
      return;
    }
    if (focusedId === nodeId) resetFocus();
    else setFocus(nodeId);
  }

  svg.addEventListener('click', onClick);

  function openDrillIn(kind: 'work' | 'theme', actId: string, nodeId: string): void {
    drilledKind = kind;
    drilledActionId = actId;
    drilledNodeId = nodeId;
    notifyDrill(true);
    overlayLayer.innerHTML = '';

    const selectedNode = nodeDataMap.get(nodeId);
    if (!selectedNode || selectedNode.angle === undefined) return;
    // Il nodo scelto ruota verso gli step: a DESTRA (angolo 0) in orizzontale,
    // in BASSO (angolo +90°) in verticale.
    const rotationRad = (portrait ? Math.PI / 2 : 0) - selectedNode.angle;

    // Clamp dello shift: il nodo selezionato deve restare INTERO nel quadro
    // (prima veniva tagliato sul bordo quando la sua orbita era stretta).
    const selR = Math.hypot(selectedNode.x - CX, selectedNode.y - CY);
    const shiftX = portrait ? 0
      : Math.min(MAP_SHIFT_X, CX + selR - selectedNode.w / 2 - U * 0.8);
    const shiftY = portrait
      ? Math.min(MAP_SHIFT_Y, CY + selR - selectedNode.h / 2 - U * 0.8)
      : 0;

    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      const nId = g.dataset.nodeId!;
      const node = nodeDataMap.get(nId);
      if (!node) return;
      const isSelected = nId === nodeId;
      const isCentral = node.type === 'central';
      let dx = 0, dy = 0;
      if (!isCentral) {
        const relX = node.x - CX;
        const relY = node.y - CY;
        const newX = relX * Math.cos(rotationRad) - relY * Math.sin(rotationRad);
        const newY = relX * Math.sin(rotationRad) + relY * Math.cos(rotationRad);
        dx = (newX + CX) - node.x;
        dy = (newY + CY) - node.y;
      }
      dx -= shiftX;
      dy -= shiftY;
      g.style.transition = 'transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease';
      g.style.transform = `translate(${dx}px, ${dy}px)`;
      g.style.opacity = isSelected ? '1' : isCentral ? '0.5' : '0.12';
      g.style.filter = isSelected ? 'drop-shadow(0 0 20px rgba(122,31,43,0.35))' : '';
    });

    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.transition = 'transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease';
      l.style.transform = `translate(${-shiftX}px, ${-shiftY}px)`;
      l.style.opacity = '0.06';
    });

    setTimeout(() => {
      if (drilledKind !== kind || drilledActionId !== actId) return;
      showSteps(kind, actId);
    }, 850);
  }

  function showSteps(kind: 'work' | 'theme', actId: string): void {
    if (kind === 'work') {
      const work = author!.works.find(w => w.id === actId && w.is_major) as MajorWork | undefined;
      if (!work) return;
      stepData = buildWorkSteps(work, locale);
    } else {
      const t = author!.theme_treatments.find(x => x.theme_id === actId);
      if (!t) return;
      const work = (author!.works.find(w => w.id === t.key_work_id && w.is_major) as MajorWork | undefined) ?? null;
      const passage = data.getPassage(author!.id, t.passage_id);
      stepData = buildThemeSteps(t, work, passage, locale);
    }
    expandedStepIdx = null;
    stepsBuilt = false;
    stepsLayer.innerHTML = '';
    renderSteps();
  }

  function buildStepsDom(): void {
    const stepW = STEPS_RIGHT - STEPS_LEFT;
    const totalSteps = stepData.length;
    const stepGap = U * 0.25;
    // Gli step riempiono la fascia dedicata (metà destra in orizzontale,
    // fascia bassa in verticale), centrati al suo interno.
    const bandH = STEPS_BOTTOM - STEPS_TOP;
    const stepH = (bandH - (totalSteps - 1) * stepGap) / totalSteps;
    const totalHeight = totalSteps * stepH + (totalSteps - 1) * stepGap;
    const startY = STEPS_TOP + (bandH - totalHeight) / 2;
    const labelFs = U * 0.85;
    const numFs = U * 1.1;

    let html = '';
    for (let i = 0; i < totalSteps; i++) {
      const s = stepData[i];
      const sy = startY + i * (stepH + stepGap);
      const borderColor = s.isClimax ? 'rgba(122,31,43,0.45)' : 'rgba(184,122,42,0.35)';
      const bgColor = s.isClimax ? 'rgba(122,31,43,0.08)' : 'rgba(184,122,42,0.10)';
      const numColor = s.isClimax ? '#7a1f2b' : '#b87a2a';

      html += `
        <g class="step-box" data-step-idx="${i}"
           data-orig-x="${STEPS_LEFT}" data-orig-y="${sy}" data-orig-w="${stepW}" data-orig-h="${stepH}"
           style="cursor:pointer">
          <rect class="step-rect" x="${STEPS_LEFT}" y="${sy}" width="${stepW}" height="${stepH}" rx="${U * 0.4}"
                fill="${bgColor}" stroke="${borderColor}" stroke-width="1.5"
                style="transition: all 0.5s cubic-bezier(0.4,0,0.2,1)" />
          <g class="step-summary" style="transition: opacity 0.3s ease">
            <text class="step-summary-num" x="${STEPS_LEFT + U * 0.9}" y="${sy + stepH / 2 + numFs * 0.18}" fill="${numColor}" font-size="${numFs}"
                  font-weight="700" font-family="Fraunces, serif">${esc(s.num)}</text>
            <foreignObject class="step-summary-label" x="${STEPS_LEFT + U * 2.4}" y="${sy}" width="${stepW - U * 2.9}" height="${stepH}">
              <div xmlns="http://www.w3.org/1999/xhtml" style="width:100%;height:100%;display:flex;flex-direction:column;justify-content:center;color:#1a1814;font-family:Fraunces,serif;font-size:${labelFs}px;font-weight:600;line-height:1.28;padding-right:${U * 0.3}px">
                <div style="font-size:${labelFs * 0.75}px;color:${numColor};letter-spacing:0.04em;text-transform:uppercase;font-weight:700;margin-bottom:${U * 0.1}px">${esc(s.title)}</div>
                <div>${esc(s.summary)}</div>
              </div>
            </foreignObject>
          </g>
          <foreignObject class="step-detail" x="${STEPS_LEFT}" y="${sy}" width="${stepW}" height="${stepH}"
                         style="opacity:0;pointer-events:none;transition:opacity 0.4s ease 0.2s">
            <div xmlns="http://www.w3.org/1999/xhtml" class="step-detail-inner" style="width:100%;height:100%;overflow-y:auto;color:#1a1814;font-family:Inter,sans-serif;font-size:${U * 0.9}px;line-height:1.7;padding:${U * 0.5}px;opacity:0;transition:opacity 0.3s ease 0.25s">
              <div style="font-family:Fraunces,serif;font-size:${U * 1.25}px;font-weight:700;color:${numColor};margin-bottom:${U * 0.4}px;line-height:1.3">
                ${esc(s.num)} — ${esc(s.title)}
              </div>
              ${s.body}
            </div>
          </foreignObject>
        </g>`;
    }
    stepsLayer.innerHTML = html;
    stepsBuilt = true;
  }

  function renderSteps(): void {
    if (!stepsBuilt) {
      buildStepsDom();
      stepsLayer.style.opacity = '0';
      stepsLayer.style.transition = 'opacity 0.5s cubic-bezier(0.4,0,0.2,1)';
      requestAnimationFrame(() => { stepsLayer.style.opacity = '1'; });
    }
    applyStepLayout();
  }

  function applyStepLayout(): void {
    const totalSteps = stepData.length;
    const stepBoxes = stepsLayer.querySelectorAll<SVGGElement>('.step-box');

    if (expandedStepIdx !== null) {
      const tabH = U * 1.1;
      const tabGap = U * 0.08;
      const expIdx = expandedStepIdx;
      const hasPrev = expIdx > 0;
      const hasNext = expIdx < totalSteps - 1;
      const topMargin = U * 0.3;
      const bottomMargin = U * 0.3;
      const prevY = topMargin;
      const expY = hasPrev ? prevY + tabH + tabGap : topMargin;
      const nextY = VH - bottomMargin - tabH;
      const expBottom = hasNext ? nextY - tabGap : VH - bottomMargin;
      const expH = expBottom - expY;
      // Espanso: in orizzontale sborda a sinistra oltre la fascia step; in
      // verticale occupa quasi tutta la larghezza dello schermo.
      const expX = portrait ? U * 0.5 : STEPS_LEFT - U * 6;
      const expW = VW - expX - U * 0.5;

      stepBoxes.forEach((g, i) => {
        const rect = g.querySelector<SVGRectElement>('.step-rect')!;
        const summaryG = g.querySelector<SVGGElement>('.step-summary')!;
        const summaryNum = g.querySelector<SVGTextElement>('.step-summary-num');
        const summaryLabel = g.querySelector<SVGForeignObjectElement>('.step-summary-label');
        const detail = g.querySelector<SVGForeignObjectElement>('.step-detail')!;
        const detailInner = detail.querySelector<HTMLElement>('.step-detail-inner');
        const isExp = i === expIdx;
        const isPrev = i === expIdx - 1;
        const isNext = i === expIdx + 1;

        if (isExp) {
          rect.setAttribute('x', String(expX));
          rect.setAttribute('y', String(expY));
          rect.setAttribute('width', String(expW));
          rect.setAttribute('height', String(expH));
          rect.setAttribute('stroke-width', '2');
          // Sfondo OPACO da espanso: il pannello copre la mappa attenuata
          // sottostante (altrimenti i nodi traspaiono dietro il testo).
          rect.style.fill = '#fbf7f0';
          summaryG.style.opacity = '0';
          summaryG.style.pointerEvents = 'none';
          detail.setAttribute('x', String(expX + U * 0.3));
          detail.setAttribute('y', String(expY + U * 0.3));
          detail.setAttribute('width', String(expW - U * 0.6));
          detail.setAttribute('height', String(expH - U * 0.6));
          detail.style.opacity = '1';
          detail.style.pointerEvents = 'auto';
          if (detailInner) detailInner.style.opacity = '1';
          g.style.opacity = '1';
          g.style.display = '';
        } else if (isPrev || isNext) {
          const tabY = isPrev ? prevY : nextY;
          rect.setAttribute('x', String(STEPS_LEFT));
          rect.setAttribute('y', String(tabY));
          rect.setAttribute('width', String(STEPS_RIGHT - STEPS_LEFT));
          rect.setAttribute('height', String(tabH));
          rect.setAttribute('stroke-width', '1');
          rect.style.fill = '';
          summaryG.style.opacity = '1';
          summaryG.style.pointerEvents = 'auto';
          if (summaryNum) summaryNum.setAttribute('y', String(tabY + tabH / 2 + U * 0.25));
          if (summaryLabel) summaryLabel.style.display = 'none';
          detail.style.opacity = '0';
          detail.style.pointerEvents = 'none';
          if (detailInner) detailInner.style.opacity = '0';
          g.style.opacity = '0.55';
          g.style.display = '';
        } else {
          g.style.display = 'none';
        }
      });
    } else {
      stepBoxes.forEach((g) => {
        const rect = g.querySelector<SVGRectElement>('.step-rect')!;
        const summaryG = g.querySelector<SVGGElement>('.step-summary')!;
        const summaryNum = g.querySelector<SVGTextElement>('.step-summary-num');
        const summaryLabel = g.querySelector<SVGForeignObjectElement>('.step-summary-label');
        const detail = g.querySelector<SVGForeignObjectElement>('.step-detail')!;
        const detailInner = detail.querySelector<HTMLElement>('.step-detail-inner');

        const ox = g.dataset.origX!;
        const oy = g.dataset.origY!;
        const ow = g.dataset.origW!;
        const oh = g.dataset.origH!;
        rect.setAttribute('x', ox);
        rect.setAttribute('y', oy);
        rect.setAttribute('width', ow);
        rect.setAttribute('height', oh);
        rect.setAttribute('stroke-width', '1.5');
        rect.style.fill = '';
        const origY = parseFloat(oy);
        const origH = parseFloat(oh);
        const numFs = U * 1.1;
        if (summaryNum) summaryNum.setAttribute('y', String(origY + origH / 2 + numFs * 0.18));
        if (summaryLabel) {
          summaryLabel.setAttribute('y', oy);
          summaryLabel.setAttribute('height', oh);
          summaryLabel.style.display = '';
        }
        summaryG.style.opacity = '1';
        summaryG.style.pointerEvents = 'auto';
        detail.style.opacity = '0';
        detail.style.pointerEvents = 'none';
        if (detailInner) detailInner.style.opacity = '0';
        g.style.opacity = '1';
        g.style.display = '';
        g.style.pointerEvents = 'auto';
      });
    }
  }

  function toggleStep(stepEl: SVGGElement): void {
    const idx = parseInt(stepEl.dataset.stepIdx ?? '-1', 10);
    if (idx < 0 || idx >= stepData.length) return;
    expandedStepIdx = expandedStepIdx === idx ? null : idx;
    renderSteps();
  }

  function closeDrillIn(): void {
    drilledKind = null;
    drilledActionId = null;
    notifyDrill(false);
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.transition = 'transform 0.7s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease';
      g.style.transform = 'translate(0,0)';
      g.style.opacity = '1';
      g.style.filter = '';
    });
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.transition = 'transform 0.7s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease';
      l.style.transform = 'translate(0,0)';
      l.style.opacity = '1';
    });
    stepsLayer.style.opacity = '0';
    setTimeout(() => {
      if (!drilledKind) {
        stepsLayer.innerHTML = '';
        stepsBuilt = false;
        expandedStepIdx = null;
      }
    }, 500);
  }

  function setFocus(nodeId: string): void {
    focusedId = nodeId;
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      const isTarget = g.dataset.nodeId === nodeId;
      g.style.transition = 'opacity 0.4s';
      g.style.opacity = isTarget ? '1' : '0.25';
      g.style.filter = isTarget ? 'drop-shadow(0 0 18px rgba(122,31,43,0.25))' : '';
    });
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.transition = 'opacity 0.4s';
      l.style.opacity = '0.15';
    });
  }

  function resetFocus(): void {
    focusedId = null;
    overlayLayer.innerHTML = '';
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.opacity = '1';
      g.style.filter = '';
    });
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.opacity = '1';
    });
  }

  // Make sure unused name doesn't trip up typecheck
  void onNavigate;

  const selectionApi: MapSelectionApi = {
    enterSelectMode: () => {
      // Non chiude il drill-in: la selezione resta sul livello in cui sei.
      selectMode = true;
      if (drilledKind) styleStepSelection();
      else styleNodeSelection();
    },
    exitSelectMode: () => {
      selectMode = false;
      selectedIds.clear();
      selectedSteps.clear();
      clearSelectionStyles();
      onSelChange?.([]);
    },
    isSelectMode: () => selectMode,
    getSelectedIds: () => currentSelectionIds(),
    clearSelection: () => {
      if (drilledKind) { selectedSteps.clear(); styleStepSelection(); }
      else { selectedIds.clear(); styleNodeSelection(); }
      onSelChange?.([]);
    },
    onSelectionChange: (cb) => { onSelChange = cb; },
    getGraph: () => ({
      kind: 'author-map',
      authorId,
      locale,
      viewBox: [0, 0, VW, VH] as [number, number, number, number],
      nodes: nodes.map((n) => ({
        id: n.id, label: n.label, type: n.type, x: n.x, y: n.y, w: n.w, h: n.h,
        ...(noteFor(n) ? { note: noteFor(n) } : {}),
      })),
      edges: edges.map((e) => ({ from: e.from, to: e.to })),
    }),
    buildCapture,
  };

  // Aggancia l'API di selezione all'elemento .author-map-view, così il
  // lesson-builder-service (che lavora generico sul DOM) può recuperarla.
  const viewEl = container.querySelector<HTMLElement>('.author-map-view');
  if (viewEl) {
    (viewEl as unknown as { __lbMapSelection?: MapSelectionApi }).__lbMapSelection = selectionApi;
  }

  return {
    dispose: () => {
      svg.removeEventListener('click', onClick);
      container.innerHTML = '';
    },
    reset: () => {
      if (!drilledKind) return false;
      closeDrillIn();
      return true;
    },
    onDrillChange: (cb) => { drillListeners.push(cb); },
    selection: selectionApi,
  };
}
